/**
 * SLOPED CEILING round (2026-10-01, Matt: "Add the ability to slope ceilings like you
 * can with Roofs, only limit the pitch to one direction (one edge can be set to
 * sloping)"). Revit's ceiling rule: one boundary line "Defines Slope"; that line stays
 * at the ceiling's height and the whole ceiling becomes ONE tilted plane rising (or,
 * with a negative slope, falling) away from it into the ceiling. This is the one place
 * the plane is derived — 3D, wall attach, the grips and hosted fixtures all read it.
 */
import type { Path2D, Pt2 } from './types'

export const CEILING_DEFAULT_SLOPE_RISE = 3

export interface CeilingSlopePlane {
  /** a point on the defining edge (its from-node) */
  a: Pt2
  /** unit plan normal pointing INTO the ceiling from the defining edge */
  n: Pt2
  /** rise per foot of run (slopeRise / 12), signed */
  m: number
  /** the defining edge's from-node id */
  nodeId: string
}

/** the slope plane of a ceiling boundary, or null when no edge defines slope */
export function ceilingSlopePlane(boundary: Path2D | null | undefined): CeilingSlopePlane | null {
  const nodes = boundary?.nodes
  if (!nodes || nodes.length < 3) return null
  const i = nodes.findIndex((nd) => nd.edge?.ceilingSlope === true)
  if (i < 0) return null
  const rise = nodes[i].edge?.slopeRise
  const m = (typeof rise === 'number' && Number.isFinite(rise) ? rise : CEILING_DEFAULT_SLOPE_RISE) / 12
  if (Math.abs(m) < 1e-9) return null
  const a = nodes[i].pt, b = nodes[(i + 1) % nodes.length].pt
  const dx = b[0] - a[0], dy = b[1] - a[1]
  const len = Math.hypot(dx, dy)
  if (len < 1e-9) return null
  // the interior is LEFT of a counter-clockwise ring, right of a clockwise one
  let area2 = 0
  for (let k = 0; k < nodes.length; k++) {
    const p = nodes[k].pt, q = nodes[(k + 1) % nodes.length].pt
    area2 += p[0] * q[1] - q[0] * p[1]
  }
  const s = area2 >= 0 ? 1 : -1
  return { a, n: [(-dy / len) * s, (dx / len) * s], m, nodeId: nodes[i].id }
}

/** the plane's height above the ceiling's base height at plan point p (feet) */
export function ceilingSlopeRiseAt(plane: CeilingSlopePlane | null, p: Pt2): number {
  if (!plane) return 0
  return plane.m * ((p[0] - plane.a[0]) * plane.n[0] + (p[1] - plane.a[1]) * plane.n[1])
}
